"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { classify, errorDetails, handleBoundaryError, reportError } from "@/lib/errorReport";

/**
 * Keeps one broken part of a page from taking the whole page down.
 *
 * Wrap a section (or let Card / Panel / DashboardShell do it for you): if
 * something inside throws while rendering, only that part is replaced by a
 * small "couldn't show this" note with a Retry button; the rest of the page,
 * the sidebar and the navbar keep working. Every catch is reported to the API
 * log (grep "Browser error" — boundary "Section: <name>").
 *
 * `resetKey`: change it (e.g. the path or the data) to try again automatically.
 * `fallback`: "card" (default, framed), "inline" (one line) or "none" (hide it —
 * for purely decorative parts such as animations).
 */
type Props = {
  children: ReactNode;
  name?: string;
  resetKey?: unknown;
  fallback?: "card" | "inline" | "none" | ReactNode;
  className?: string;
};

type State = { error: Error | null; tries: number; copied: boolean };

export class SafeBoundary extends Component<Props, State> {
  state: State = { error: null, tries: 0, copied: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    try {
      const err = error instanceof Error ? error : new Error(String(error));
      const stack = `${err.stack ?? ""}\n--- component stack ---${info?.componentStack ?? ""}`;
      // A browser extension or page translation changing the page ("dom") clears up on a
      // fresh render: try once by itself before showing anything.
      const kind = classify(err);
      // An out-of-date copy of the app (a tab left open across a deploy: a page's old code meets the new
      // shared code, "(0 , x.y) is not a function", or a code file is gone) only clears up with a reload.
      // Reload once, like the page error screen does, instead of leaving "This part couldn't be shown".
      if (kind === "chunk") {
        handleBoundaryError(Object.assign(err, { stack }), `Section: ${this.props.name ?? "unnamed"}`, this.retry);
        return;
      }
      reportError(Object.assign(err, { stack }), `Section: ${this.props.name ?? "unnamed"}`, kind === "dom" && this.state.tries === 0);
      if (kind === "dom" && this.state.tries === 0) setTimeout(() => this.retry(), 0);
    } catch {
      /* reporting must never cause a second error */
    }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && !Object.is(prev.resetKey, this.props.resetKey)) this.setState({ error: null });
  }

  retry = () => this.setState((s) => ({ error: null, tries: s.tries + 1, copied: false }));

  // "Try again" on an out-of-date copy of the app reloads the page: rendering again would only fail again.
  tryAgain = () => {
    if (this.state.error && classify(this.state.error) === "chunk") window.location.reload();
    else this.retry();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const { fallback = "card", className } = this.props;
    if (fallback === "none") return null;
    if (fallback !== "card" && fallback !== "inline") return fallback;

    if (fallback === "inline")
      return (
        <span role="alert" className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-rb-muted ${className ?? ""}`}>
          <AlertTriangle size={13} className="text-rb-mid" />
          Couldn&apos;t show this.
          <button type="button" onClick={this.tryAgain} className="font-bold text-rb-brand underline-offset-2 hover:underline">
            Retry
          </button>
        </span>
      );

    return (
      <div
        role="alert"
        className={`flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-rb-line bg-rb-surface-2/60 px-5 py-7 text-center ${className ?? ""}`}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rb-mid-soft text-rb-mid">
          <AlertTriangle size={18} />
        </span>
        <p className="text-[13.5px] font-bold text-rb-ink">This part couldn&apos;t be shown</p>
        <p className="max-w-[40ch] text-[12.5px] text-rb-muted">The rest of the page still works. It&apos;s been reported to us.</p>
        <button
          type="button"
          onClick={this.tryAgain}
          className="mt-1 inline-flex h-9 items-center gap-1.5 rounded-full bg-white px-4 text-[12.5px] font-bold text-rb-brand-deep ring-1 ring-rb-line transition-colors hover:bg-rb-brand-soft"
        >
          <RotateCcw size={13} /> Try again
        </button>
        {/* What went wrong, so one screenshot or paste tells support exactly where to look. */}
        <details className="mt-1 max-w-[560px] text-left text-[11.5px] text-rb-muted">
          <summary className="cursor-pointer text-center font-semibold hover:text-rb-ink">Error details</summary>
          <p className="mt-2 break-words rounded-lg bg-white px-3 py-2 font-mono text-[11px] text-rb-ink ring-1 ring-rb-line">
            {this.props.name ? `${this.props.name}: ` : ""}
            {this.state.error.name}: {this.state.error.message}
          </p>
          <button
            type="button"
            className="mt-1.5 font-semibold text-rb-brand-deep hover:underline"
            onClick={() => {
              const err = this.state.error;
              if (!err || !navigator.clipboard) return;
              navigator.clipboard.writeText(errorDetails(err)).then(() => this.setState({ copied: true })).catch(() => undefined);
            }}
          >
            {this.state.copied ? "Copied" : "Copy details"}
          </button>
        </details>
      </div>
    );
  }
}
